// =============================================================================
// GOUVERNANCE - Module Coûts OmniHub
// =============================================================================
// Suivi des coûts de tous les modules et APIs facturées
// Twilio, Emergent LLM, DocuSign, Stockage, etc.
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Bot,
  Mail,
  FileSignature,
  HardDrive,
  Phone,
  RefreshCw,
  AlertTriangle,
  Settings,
  Calendar,
  ChevronDown,
  BarChart3,
  PieChart,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Download,
  Bell
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Progress } from "../../../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des services avec leurs icônes et couleurs
const SERVICE_CONFIG = {
  twilio_sms: {
    name: "Twilio SMS",
    icon: MessageSquare,
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    unit: "SMS",
    defaultRate: 0.0079 // €/SMS
  },
  twilio_voice: {
    name: "Twilio Voice",
    icon: Phone,
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    unit: "minute",
    defaultRate: 0.015 // €/min
  },
  emergent_llm: {
    name: "Emergent LLM",
    icon: Bot,
    color: "text-violet-400",
    bgColor: "bg-violet-500/10",
    unit: "token (1K)",
    defaultRate: 0.002 // €/1K tokens
  },
  docusign: {
    name: "DocuSign",
    icon: FileSignature,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    unit: "enveloppe",
    defaultRate: 1.50 // €/enveloppe
  },
  email: {
    name: "Email (SMTP)",
    icon: Mail,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    unit: "email",
    defaultRate: 0.001 // €/email (SendGrid, etc.)
  },
  storage: {
    name: "Stockage",
    icon: HardDrive,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    unit: "Go",
    defaultRate: 0.023 // €/Go/mois
  },
  other: {
    name: "Autres APIs",
    icon: Settings,
    color: "text-slate-400",
    bgColor: "bg-slate-500/10",
    unit: "appel",
    defaultRate: 0.01
  }
};

export default function GouvCosts({ tenantId }) {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("month"); // month, year, custom
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState({
    total_month: 0,
    total_year: 0,
    budget_month: 500,
    budget_year: 6000,
    trend: 0
  });
  const [alerts, setAlerts] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showAddCostDialog, setShowAddCostDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [budgetConfig, setBudgetConfig] = useState({
    budget_month: 500,
    budget_year: 6000,
    alert_threshold: 80, // % du budget
    email_alerts: true
  });
  const [newCost, setNewCost] = useState({
    service: "twilio_sms",
    quantity: 0,
    unit_price: 0,
    description: "",
    date: new Date().toISOString().split('T')[0]
  });

  // Charger les données
  const loadCosts = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { "X-Tenant-ID": tenantId || "climatpac" };
      
      // Charger les coûts
      const res = await fetch(`${API}/api/governance/costs?period=${period}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCosts(data.costs || []);
        setSummary(data.summary || summary);
        setAlerts(data.alerts || []);
      }
      
      // Charger la config budget
      const configRes = await fetch(`${API}/api/governance/costs/config`, { headers });
      if (configRes.ok) {
        const config = await configRes.json();
        setBudgetConfig(prev => ({ ...prev, ...config }));
      }
    } catch (error) {
      console.error("Erreur chargement coûts:", error);
      // Données de démo si l'API n'existe pas encore
      loadDemoData();
    } finally {
      setLoading(false);
    }
  }, [tenantId, period]);

  // Données de démonstration
  const loadDemoData = () => {
    const demoCosts = [
      { id: "1", service: "twilio_sms", quantity: 1250, unit_price: 0.0079, total: 9.88, date: "2026-02-25", description: "SMS notifications" },
      { id: "2", service: "emergent_llm", quantity: 450, unit_price: 0.002, total: 0.90, date: "2026-02-25", description: "Tokens GPT-4o (450K)" },
      { id: "3", service: "docusign", quantity: 12, unit_price: 1.50, total: 18.00, date: "2026-02-24", description: "Signatures devis" },
      { id: "4", service: "storage", quantity: 2.5, unit_price: 0.023, total: 0.06, date: "2026-02-01", description: "Stockage fichiers" },
      { id: "5", service: "twilio_voice", quantity: 45, unit_price: 0.015, total: 0.68, date: "2026-02-20", description: "Appels sortants" },
    ];
    
    setCosts(demoCosts);
    
    const totalMonth = demoCosts.reduce((sum, c) => sum + c.total, 0);
    setSummary({
      total_month: totalMonth,
      total_year: totalMonth * 12,
      budget_month: 500,
      budget_year: 6000,
      trend: 5.2
    });
  };

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  // Sauvegarder la configuration
  const saveConfig = async () => {
    try {
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/governance/costs/config`, {
        method: "POST",
        headers,
        body: JSON.stringify(budgetConfig)
      });
      
      if (res.ok) {
        toast.success("Configuration sauvegardée");
        setShowConfigDialog(false);
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.success("Configuration sauvegardée (mode démo)");
      setShowConfigDialog(false);
    }
  };

  // Ajouter un coût manuellement
  const addCost = async () => {
    try {
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json"
      };
      
      const total = newCost.quantity * newCost.unit_price;
      const costData = { ...newCost, total };
      
      const res = await fetch(`${API}/api/governance/costs`, {
        method: "POST",
        headers,
        body: JSON.stringify(costData)
      });
      
      if (res.ok) {
        toast.success("Coût ajouté");
        setShowAddCostDialog(false);
        loadCosts();
      } else {
        // Mode démo
        setCosts(prev => [...prev, { ...costData, id: Date.now().toString() }]);
        toast.success("Coût ajouté (mode démo)");
        setShowAddCostDialog(false);
      }
    } catch (error) {
      // Mode démo
      const total = newCost.quantity * newCost.unit_price;
      setCosts(prev => [...prev, { ...newCost, total, id: Date.now().toString() }]);
      toast.success("Coût ajouté (mode démo)");
      setShowAddCostDialog(false);
    }
  };

  // Supprimer un coût
  const deleteCost = async (costId) => {
    if (!confirm("Supprimer cette entrée ?")) return;
    
    try {
      const headers = { "X-Tenant-ID": tenantId || "climatpac" };
      await fetch(`${API}/api/governance/costs/${costId}`, {
        method: "DELETE",
        headers
      });
      setCosts(prev => prev.filter(c => c.id !== costId));
      toast.success("Entrée supprimée");
    } catch (error) {
      setCosts(prev => prev.filter(c => c.id !== costId));
      toast.success("Entrée supprimée (mode démo)");
    }
  };

  // Calculer les totaux par service
  const costsByService = costs.reduce((acc, cost) => {
    if (!acc[cost.service]) {
      acc[cost.service] = { total: 0, quantity: 0, count: 0 };
    }
    acc[cost.service].total += cost.total;
    acc[cost.service].quantity += cost.quantity;
    acc[cost.service].count += 1;
    return acc;
  }, {});

  // Pourcentage du budget utilisé
  const budgetUsedPercent = (summary.total_month / summary.budget_month) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-emerald-400" />
            Coûts OmniHub
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Suivi des dépenses modules et APIs
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddCostDialog(true)}
            className="border-emerald-500/50 text-emerald-400"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfigDialog(true)}
            className="border-slate-600"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadCosts}
            disabled={loading}
            className="border-slate-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alertes */}
      {budgetUsedPercent >= budgetConfig.alert_threshold && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <div>
            <p className="text-amber-400 font-medium">Attention au budget !</p>
            <p className="text-amber-400/70 text-sm">
              Vous avez utilisé {budgetUsedPercent.toFixed(0)}% de votre budget mensuel
            </p>
          </div>
        </div>
      )}

      {/* Cards résumé */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total mois */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Dépenses du mois</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {summary.total_month.toFixed(2)} €
                </p>
              </div>
              <div className={`p-3 rounded-lg ${summary.trend >= 0 ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
                {summary.trend >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-red-400" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-emerald-400" />
                )}
              </div>
            </div>
            <p className={`text-xs mt-2 ${summary.trend >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {summary.trend >= 0 ? '+' : ''}{summary.trend}% vs mois dernier
            </p>
          </CardContent>
        </Card>

        {/* Budget restant */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Budget restant</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">
                  {(summary.budget_month - summary.total_month).toFixed(2)} €
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <BarChart3 className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <div className="mt-2">
              <Progress 
                value={Math.min(budgetUsedPercent, 100)} 
                className="h-2"
              />
              <p className="text-xs text-slate-500 mt-1">
                {budgetUsedPercent.toFixed(0)}% utilisé sur {summary.budget_month}€
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Prévision année */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Prévision annuelle</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {(summary.total_month * 12).toFixed(0)} €
                </p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Calendar className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Budget: {summary.budget_year}€/an
            </p>
          </CardContent>
        </Card>

        {/* Nombre de services */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Services actifs</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {Object.keys(costsByService).length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-violet-500/10">
                <PieChart className="w-6 h-6 text-violet-400" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {costs.length} transactions ce mois
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Par service / Détail / Historique */}
      <Tabs defaultValue="services" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="services">Par service</TabsTrigger>
          <TabsTrigger value="detail">Détail</TabsTrigger>
          <TabsTrigger value="chart">Graphique</TabsTrigger>
        </TabsList>

        {/* Par service */}
        <TabsContent value="services">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(costsByService).map(([serviceKey, data]) => {
              const config = SERVICE_CONFIG[serviceKey] || SERVICE_CONFIG.other;
              const Icon = config.icon;
              return (
                <Card 
                  key={serviceKey}
                  className={`bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer`}
                  onClick={() => setSelectedService(serviceKey)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.bgColor}`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{config.name}</p>
                        <p className="text-slate-400 text-sm">
                          {data.quantity.toLocaleString()} {config.unit}(s)
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{data.total.toFixed(2)} €</p>
                        <p className="text-slate-500 text-xs">{data.count} entrées</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {Object.keys(costsByService).length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-400">
                Aucune dépense enregistrée pour cette période
              </div>
            )}
          </div>
        </TabsContent>

        {/* Détail */}
        <TabsContent value="detail">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Service</TableHead>
                    <TableHead className="text-slate-400">Description</TableHead>
                    <TableHead className="text-slate-400 text-right">Quantité</TableHead>
                    <TableHead className="text-slate-400 text-right">Prix unit.</TableHead>
                    <TableHead className="text-slate-400 text-right">Total</TableHead>
                    <TableHead className="text-slate-400 w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costs.map((cost) => {
                    const config = SERVICE_CONFIG[cost.service] || SERVICE_CONFIG.other;
                    const Icon = config.icon;
                    return (
                      <TableRow key={cost.id} className="border-slate-700">
                        <TableCell className="text-slate-300 font-mono text-sm">
                          {cost.date}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${config.color}`} />
                            <span className="text-white">{config.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm max-w-[200px] truncate">
                          {cost.description || "-"}
                        </TableCell>
                        <TableCell className="text-right text-white">
                          {cost.quantity.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-slate-400 font-mono">
                          {cost.unit_price.toFixed(4)} €
                        </TableCell>
                        <TableCell className="text-right text-emerald-400 font-bold">
                          {cost.total.toFixed(2)} €
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteCost(cost.id)}
                            className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {costs.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  Aucune dépense enregistrée
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Graphique (placeholder) */}
        <TabsContent value="chart">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center">
              <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Graphique d'évolution des coûts</p>
              <p className="text-slate-500 text-sm mt-2">
                Visualisation mensuelle à venir
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Configuration budget */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuration des budgets
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Budget mensuel (€)</Label>
              <Input
                type="number"
                value={budgetConfig.budget_month}
                onChange={(e) => setBudgetConfig(prev => ({ 
                  ...prev, 
                  budget_month: parseFloat(e.target.value) || 0 
                }))}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Budget annuel (€)</Label>
              <Input
                type="number"
                value={budgetConfig.budget_year}
                onChange={(e) => setBudgetConfig(prev => ({ 
                  ...prev, 
                  budget_year: parseFloat(e.target.value) || 0 
                }))}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Seuil d'alerte (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={budgetConfig.alert_threshold}
                onChange={(e) => setBudgetConfig(prev => ({ 
                  ...prev, 
                  alert_threshold: parseInt(e.target.value) || 80 
                }))}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Alerte quand ce % du budget est atteint
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Alertes par email</Label>
              <Switch
                checked={budgetConfig.email_alerts}
                onCheckedChange={(checked) => setBudgetConfig(prev => ({ 
                  ...prev, 
                  email_alerts: checked 
                }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfigDialog(false)}
              className="border-slate-600"
            >
              Annuler
            </Button>
            <Button
              onClick={saveConfig}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ajouter un coût */}
      <Dialog open={showAddCostDialog} onOpenChange={setShowAddCostDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Ajouter une dépense
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Service</Label>
              <Select
                value={newCost.service}
                onValueChange={(value) => {
                  const config = SERVICE_CONFIG[value];
                  setNewCost(prev => ({ 
                    ...prev, 
                    service: value,
                    unit_price: config?.defaultRate || 0
                  }));
                }}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SERVICE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Date</Label>
              <Input
                type="date"
                value={newCost.date}
                onChange={(e) => setNewCost(prev => ({ ...prev, date: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">
                  Quantité ({SERVICE_CONFIG[newCost.service]?.unit || 'unité'})
                </Label>
                <Input
                  type="number"
                  value={newCost.quantity}
                  onChange={(e) => setNewCost(prev => ({ 
                    ...prev, 
                    quantity: parseFloat(e.target.value) || 0 
                  }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Prix unitaire (€)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={newCost.unit_price}
                  onChange={(e) => setNewCost(prev => ({ 
                    ...prev, 
                    unit_price: parseFloat(e.target.value) || 0 
                  }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Description</Label>
              <Input
                value={newCost.description}
                onChange={(e) => setNewCost(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: SMS notifications février"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-slate-400 text-sm">Total estimé</p>
              <p className="text-2xl font-bold text-emerald-400">
                {(newCost.quantity * newCost.unit_price).toFixed(2)} €
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddCostDialog(false)}
              className="border-slate-600"
            >
              Annuler
            </Button>
            <Button
              onClick={addCost}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
